import { createClient } from '@supabase/supabase-js';

// Access the Vercel/Vite environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Safety check to debug connection issues
if (!supabaseUrl || !supabaseKey) {
  console.error('⚠️ Supabase keys are missing! Check your .env file or Vercel settings.');
}

/**
 * HybridStorage - Seamless storage adapter that works in all environments
 * 
 * Tries localStorage first, silently falls back to in-memory storage on failure.
 * This handles incognito mode, Safari private browsing, and storage quota issues
 * without any hangs or errors - just works.
 */
class HybridStorage implements Storage {
  private memoryStore = new Map<string, string>();
  private useMemory = false;
  
  constructor() {
    // Test localStorage availability once at construction
    this.useMemory = !this.testLocalStorage();
    console.log(`[Supabase] Storage mode: ${this.useMemory ? 'Memory (incognito)' : 'localStorage'}`);
  }
  
  private testLocalStorage(): boolean {
    try {
      const testKey = '__storage_test__';
      const testValue = `test_${Date.now()}`;
      localStorage.setItem(testKey, testValue);
      const retrieved = localStorage.getItem(testKey);
      localStorage.removeItem(testKey);
      return retrieved === testValue;
    } catch {
      return false;
    }
  }
  
  get length(): number {
    if (this.useMemory) {
      return this.memoryStore.size;
    }
    try {
      return localStorage.length;
    } catch {
      return this.memoryStore.size;
    }
  }
  
  key(index: number): string | null {
    if (this.useMemory) {
      const keys = Array.from(this.memoryStore.keys());
      return keys[index] ?? null;
    }
    try {
      return localStorage.key(index);
    } catch {
      const keys = Array.from(this.memoryStore.keys());
      return keys[index] ?? null;
    }
  }
  
  clear(): void {
    if (this.useMemory) {
      this.memoryStore.clear();
      return;
    }
    try {
      localStorage.clear();
    } catch {
      this.useMemory = true;
      this.memoryStore.clear();
    }
  }
  
  getItem(key: string): string | null {
    if (this.useMemory) {
      return this.memoryStore.get(key) ?? null;
    }
    try {
      return localStorage.getItem(key);
    } catch {
      // localStorage failed at runtime, switch to memory
      this.useMemory = true;
      return this.memoryStore.get(key) ?? null;
    }
  }
  
  setItem(key: string, value: string): void {
    if (this.useMemory) {
      this.memoryStore.set(key, value);
      return;
    }
    try {
      localStorage.setItem(key, value);
    } catch {
      // localStorage failed (quota exceeded, blocked, etc.) - switch to memory
      this.useMemory = true;
      this.memoryStore.set(key, value);
    }
  }
  
  removeItem(key: string): void {
    if (this.useMemory) {
      this.memoryStore.delete(key);
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch {
      this.useMemory = true;
      this.memoryStore.delete(key);
    }
  }
}

// Create the storage adapter
const storage = new HybridStorage();

// Export for checking if localStorage is available (used by pages for sessionStorage access)
export const isLocalStorageAvailable = !storage['useMemory'];

/**
 * Supabase client - single instance with HybridStorage
 * Works seamlessly in both normal and incognito modes
 */
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    storage: storage,
    detectSessionInUrl: true,
    autoRefreshToken: true,
    storageKey: 'padswipe-auth',
    flowType: 'implicit',
  },
});

// NOTE: Don't add onAuthStateChange listener at module level - it can interfere with
// Supabase's internal token processing and cause deadlocks

/**
 * Get the Supabase client instance
 * Alias for backward compatibility with getSupabase() pattern
 */
export function getSupabase() {
  return supabase;
}
